import apiClient from "./apiClient";
import { isAuthenticated } from "./auth";
import { sanitizePublicProduct } from "@/utils/publicCatalog";
import { isAdminSession } from '@/utils/adminFreshness';
import { apiErrorMessage } from '@/utils/apiErrors';
import { imageFileError } from '@/utils/imageUploads';

export async function getRelatedProducts(id) {
  const { data } = await apiClient.get(`/products/${id}/related/`, { skipAppLoader: true });
  return (data.results || []).map(sanitizePublicProduct);
}

const CACHE_DURATION = 60 * 1000; // 1 minuto
const PAGINATED_CACHE_PREFIX = 'products_page_';
const memoryPageCache = new Map();
const inFlightRequests = new Map();
let cacheGeneration = 0;

const isCacheValid = (timestamp, maxAge = CACHE_DURATION) => {
  if (!timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp, 10);
  return (now - lastUpdate) < maxAge;
};

const sanitizeForCatalog = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => (item?.catalog_only ? sanitizePublicProduct(item) : item))
  }
  return data?.catalog_only ? sanitizePublicProduct(data) : data
}

const buildPaginatedCacheKey = (page, pageSize, filters, namespace = '') => {
  const normalizedFilters = Object.keys(filters || {})
    .sort()
    .reduce((acc, key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {});

  return JSON.stringify({
    ns: namespace,
    page,
    pageSize,
    filters: normalizedFilters,
    auth: isAuthenticated(),
    user: localStorage.getItem('userId'),
    admin: isAdminSession(),
  });
};

const hashCacheKey = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return `${PAGINATED_CACHE_PREFIX}${hash}`;
};

const readPaginatedCache = (cacheKey) => {
  const memoryEntry = memoryPageCache.get(cacheKey);
  if (memoryEntry) {
    return memoryEntry;
  }

  try {
    const storageKey = hashCacheKey(cacheKey);
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.data) {
      memoryPageCache.set(cacheKey, parsed);
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};

const writePaginatedCache = (cacheKey, data) => {
  const entry = { data, timestamp: Date.now().toString() };
  memoryPageCache.set(cacheKey, entry);

  try {
    sessionStorage.setItem(hashCacheKey(cacheKey), JSON.stringify(entry));
  } catch {
    // sessionStorage lleno: conservar al menos la caché en memoria
  }
};

const clearPaginatedCache = () => {
  memoryPageCache.clear();
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(PAGINATED_CACHE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // noop
  }
};

export const invalidateProductsCache = () => {
  cacheGeneration += 1;
  inFlightRequests.clear();
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  localStorage.removeItem('admin_products');
  localStorage.removeItem('admin_productsTimestamp');
  clearPaginatedCache();
};

const PRODUCTS_CHANGED_KEY = 'productsChangedAt';
const notifyProductsChanged = () => {
  invalidateProductsCache();
  try { localStorage.setItem(PRODUCTS_CHANGED_KEY, `${Date.now()}-${Math.random()}`); } catch { /* storage may be disabled */ }
  if (typeof window.dispatchEvent === 'function') window.dispatchEvent(new Event('products-changed'));
};

export const onProductsChanged = (listener) => {
  const external = (event) => {
    if (event.key !== PRODUCTS_CHANGED_KEY || !event.newValue) return;
    invalidateProductsCache();
    listener();
  };
  window.addEventListener('products-changed', listener);
  window.addEventListener('storage', external);
  return () => {
    window.removeEventListener('products-changed', listener);
    window.removeEventListener('storage', external);
  };
};

const normalizePaginatedResponse = (response) => {
  let result = {
    results: [],
    count: 0,
    next: null,
    previous: null,
  };

  if (response.data?.results) {
    result = {
      ...response.data,
      count: typeof response.data.count === 'number'
        ? response.data.count
        : (response.data.results?.length || 0),
    };
  } else if (Array.isArray(response.data)) {
    result.results = response.data;
    result.count = response.data.length;
  }

  if (result.results?.length) {
    result.results = sanitizeForCatalog(result.results);
  }

  return result;
};

const fetchProductsPaginatedFromApi = async (page, pageSize, filters) => {
  const isAuth = isAuthenticated();
  const headers = {};
  if (isAuth) {
    const token = localStorage.getItem("accessToken");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  let url = `/products/?page=${page}&page_size=${pageSize}`;

  if (filters.type?.length) url += `&type=${filters.type.join(',')}`;
  if (filters.excludeTypes?.length) url += `&exclude_types=${filters.excludeTypes.join(',')}`;
  if (filters.brands?.length) url += `&brand=${filters.brands.join(',')}`;
  if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
  if (filters.onlyDiscounted) url += '&discount=true';
  if (filters.priceRange?.length) url += `&priceRange=${filters.priceRange.join(',')}`;
  if (filters.stockFilter?.length) url += `&stockFilter=${filters.stockFilter.join(',')}`;
  if (filters.noStock) url += '&no_stock=true';
  if (filters.ordering) url += `&ordering=${filters.ordering}`;
  if (filters.highlighted === true) url += '&highlighted=true';
  if (filters.limitedEdition === true) url += '&limitedEdition=true';
  // El merchandising debe seguir siendo visible para visitantes aunque no
  // tenga stock/disponibilidad de venta configurada. El filtro de available
  // se mantiene para el catálogo de semillas.
  const isMerchCatalog = filters.type?.includes('merch');
  if (!isAuth && !isMerchCatalog) url += '&available=true';

  const response = await apiClient.get(url, { headers });
  return normalizePaginatedResponse(response);
};

const refreshPaginatedCache = async (cacheKey, page, pageSize, filters) => {
  const generation = cacheGeneration;
  try {
    const freshData = await fetchProductsPaginatedFromApi(page, pageSize, filters);
    if (generation === cacheGeneration && !isAdminSession()) writePaginatedCache(cacheKey, freshData);
    return freshData;
  } finally {
    if (generation === cacheGeneration) inFlightRequests.delete(cacheKey);
  }
};


// Nueva función para obtener productos con paginación
export const getProductsPaginated = async (page = 1, pageSize = 9, filters = {}, options = {}) => {
  const { ignoreCache = false } = options;
  if (isAdminSession()) return fetchProductsPaginatedFromApi(page, pageSize, filters);
  if (ignoreCache) invalidateProductsCache();
  const cacheKey = buildPaginatedCacheKey(page, pageSize, filters);

  try {
    if (!ignoreCache) {
      const cached = readPaginatedCache(cacheKey);
      if (cached && isCacheValid(cached.timestamp)) {
        return cached.data;
      }

    }

    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey);
    }

    const requestPromise = refreshPaginatedCache(cacheKey, page, pageSize, filters);
    inFlightRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  } catch (error) {
    console.error('Error fetching paginated products:', error);
    throw error;
  }
};

export const getProducts = async (ignoreCache = false, page = 1, pageSize = 20) => {
  try {
    const cachedData = localStorage.getItem('products');
    const cacheTimestamp = localStorage.getItem('productsTimestamp');
    const isAuth = isAuthenticated();
    
    // Solo usar caché para la primera página
    if (!isAdminSession() && !ignoreCache && page === 1 && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      console.log('Usando caché de productos');
      let products = JSON.parse(cachedData);
      
      // Filtrar productos no disponibles para usuarios no autenticados
      if (!isAuth) {
        products = products.filter(product => product.available !== false);
      }
      
      products = sanitizeForCatalog(products);
      return { results: products, count: products.length, next: null, previous: null };
    }

    console.log(`Obteniendo productos página ${page}`);
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    if (result.results?.length) {
      result.results = sanitizeForCatalog(result.results);
    }

    // Solo guardar en caché la primera página
    if (!isAdminSession() && page === 1) {
      localStorage.setItem('products', JSON.stringify(result.results));
      localStorage.setItem('productsTimestamp', Date.now().toString());
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función específica para admin que devuelve productos paginados sin cargar todo.
// Siempre consulta la API: una edición no debe reutilizar respuestas previas.
const buildAdminProductsUrl = (page, pageSize, filters = {}) => {
  let url = `/products/?page=${page}&page_size=${pageSize}`;

  if (filters.search) {
    url += `&search=${encodeURIComponent(filters.search)}`;
  }
  if (filters.brand) {
    url += `&brand=${encodeURIComponent(filters.brand)}`;
  }
  if (filters.type) {
    url += `&type=${encodeURIComponent(filters.type)}`;
  }
  if (filters.availability) {
    if (filters.availability === 'available') {
      url += `&available=true`;
    } else if (filters.availability === 'unavailable') {
      url += `&available=false`;
    }
  }
  if (filters.stockFilter) {
    url += `&stockFilter=${encodeURIComponent(filters.stockFilter)}`;
  }
  if (filters.priceStatus) {
    url += `&priceStatus=${encodeURIComponent(filters.priceStatus)}`;
  }
  if (filters.noStock) {
    url += `&no_stock=true`;
  }
  if (filters.limitedEdition !== null && filters.limitedEdition !== undefined) {
    url += `&limitedEdition=${filters.limitedEdition}`;
  }

  return url;
};

const fetchAdminProductsFromApi = async (page, pageSize, filters) => {
  const token = localStorage.getItem('accessToken');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await apiClient.get(buildAdminProductsUrl(page, pageSize, filters), { headers });

  let result = {
    results: [],
    count: 0,
    next: null,
    previous: null,
  };

  if (response.data && response.data.results) {
    result = response.data;
  } else if (Array.isArray(response.data)) {
    result.results = response.data;
    result.count = response.data.length;
  }

  return result;
};

export const getAllProductsForAdmin = async (...args) => {
  // Se conserva el primer argumento (ignoreCache) por compatibilidad.
  const [, page = 1, pageSize = 20, filters = {}] = args;
  return fetchAdminProductsFromApi(page, pageSize, filters);
};

// El backend conserva las credenciales y carga la imagen en Bunny Storage.
export const uploadImage = async (file, options = {}) => {
  const validation = imageFileError(file);
  if (validation) throw new Error(validation);
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await apiClient.post('/media/images/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      signal: options.signal,
      onUploadProgress: options.onUploadProgress,
    });
    const url = response.data?.image_url;
    if (typeof url !== 'string' || !url.startsWith('https://')) {
      throw new Error('El servidor no devolvió la dirección de la foto. No se agregó al producto; intentá nuevamente.');
    }
    return url;
  } catch (error) {
    if (error.code === 'ERR_CANCELED') throw error;
    throw new Error(apiErrorMessage(error, error.response || error.request ? 'No se pudo subir la foto.' : error.message));
  }
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/');
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Función para obtener productos destacados (highlighted)
export const getHighlightedProducts = async (limit = 3) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/highlighted/?limit=${limit}`, { headers });
    
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    
    if (response.data.results) {
      response.data.results = sanitizeForCatalog(response.data.results);
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching highlighted products:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Nueva función para obtener productos más recientes con stock y precio
export const getLatestProductsWithStock = async (limit = 8) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Endpoint para obtener productos más recientes con stock y precio
    let url = `/products/?ordering=-created_at&has_stock=true&has_price=true&page_size=${limit}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    if (result.results?.length) {
      result.results = sanitizeForCatalog(result.results);
    }

    return result;
  } catch (error) {
    console.error('Error fetching latest products with stock:', error);
    return { results: [] };
  }
};

export const getProduct = async (id) => {
  try {
    // La ficha completa nunca se reconstruye desde un listado parcial.
    
    // Incluir token de autenticación si el usuario está autenticado
    const headers = {};
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/${id}/`, { headers });
    console.log('Product API response:', response.data);

    return sanitizeForCatalog(response.data);
  } catch (error) {
    console.error('Error in getProduct:', error);
    console.error('Error response:', error.response?.data);
    throw new Error(apiErrorMessage(error, 'No se pudo cargar el producto.'));
  }
};

// Función para crear un nuevo producto
export const createProduct = async (productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Creando producto:', productData);
    const response = await apiClient.post("/products/", productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo crear el producto.'));
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Actualizando producto:', productId, productData);
    const response = await apiClient.put(`/products/${productId}/`, productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo guardar el producto.'));
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo eliminar el producto.'));
  }
};

export const getBlisterStock = async (blisterId) => {
  try {
    const response = await apiClient.get(`/products/blisters/${blisterId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blister stock:', error);
    throw error;
  }
};

// Visibilidad en el catálogo web público (PublicCatalogListing)
export const setProductPublicWebVisibility = async (productId, showOnPublicWeb) => {
  try {
    const showBool = showOnPublicWeb === true || showOnPublicWeb === 'true'
    const response = await apiClient.patch(`/products/${productId}/`, { show_on_public_web: showBool })
    notifyProductsChanged()
    return response.data
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo cambiar la visibilidad en la web.'))
  }
}

// Función para cambiar el estado de disponibilidad de un producto (solo admin)
export const toggleProductAvailability = async (productId, available) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando disponibilidad del producto:', productId, 'a:', available);
    const response = await apiClient.patch(`/products/${productId}/`, { available }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo cambiar la disponibilidad del producto.'));
  }
};

// Función para cambiar el estado destacado de un producto (solo admin)
export const toggleProductHighlighted = async (productId, highlighted) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando estado destacado del producto:', productId, 'a:', highlighted);
    // Ensure we're sending a boolean value, not a string
    const highlightedBool = highlighted === true || highlighted === 'true';
    const response = await apiClient.patch(`/products/${productId}/`, { highlighted: highlightedBool }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo cambiar el estado destacado del producto.'));
  }
};

// Función para cambiar el estado de edición limitada de un producto (solo admin)
export const toggleProductLimitedEdition = async (productId, limitedEdition) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando estado de edición limitada del producto:', productId, 'a:', limitedEdition);
    // Ensure we're sending a boolean value, not a string
    const limitedEditionBool = limitedEdition === true || limitedEdition === 'true';
    const response = await apiClient.patch(`/products/${productId}/`, { limited_edition: limitedEditionBool }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    notifyProductsChanged();
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'No se pudo cambiar la edición limitada del producto.'));
  }
};
